import type {
  ButtonHTMLAttributes,
  HTMLAttributes,
  InputHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";

import { cn } from "@/ui/lib";
import { DefinedDefaults } from "@/ui/lib/types";
import {
  BASE_TEXT_BODY_SIZE_CLASS_MAP,
} from "@/ui/components/typography/TextBody";
import {
  type TButtonVariant,
} from "@/ui/components/primitives/Button/Button.config";

/*-------------------------------------
|  Constants
-------------------------------------*/

const {
  2: INPUT_GROUP_CONTROL_TEXT_SIZE_CLASS,
  3: INPUT_GROUP_TEXT_SIZE_CLASS,
  4: INPUT_GROUP_BUTTON_XS_TEXT_SIZE_CLASS,
} = BASE_TEXT_BODY_SIZE_CLASS_MAP;

export const INPUT_GROUP_CLASS = cn(
  "group/input-group relative flex w-full items-center",
  "rounded-sys-sm border border-solid border-component-input-default bg-component-input-default shadow-sm",
  "font-sys-default transition-colors",
  "has-[textarea]:items-stretch",
  "has-[[data-align=block-start]]:flex-col has-[[data-align=block-start]]:items-stretch",
  "has-[[data-align=block-end]]:flex-col has-[[data-align=block-end]]:items-stretch",
  // Control-scoped focus: only the input/textarea drives the group's focus ring,
  // never a focusable addon (button, tooltip trigger) — mirrors shadcn's scope.
  "has-[input:focus]:bg-component-input-focused has-[textarea:focus]:bg-component-input-focused",
  "has-[input:focus]:outline has-[input:focus]:outline-1 has-[input:focus]:outline-offset-0 has-[input:focus]:outline-sys-black",
  "has-[textarea:focus]:outline has-[textarea:focus]:outline-1 has-[textarea:focus]:outline-offset-0 has-[textarea:focus]:outline-sys-black",
  "has-[input:focus]:ring-4 has-[input:focus]:ring-focus-primary has-[textarea:focus]:ring-4 has-[textarea:focus]:ring-focus-primary",
  "has-[[aria-invalid=true]]:border-component-input-destructive",
  "has-[[aria-invalid=true]:focus]:outline-transparent has-[[aria-invalid=true]:focus]:ring-focus-destructive",
  "has-[>input:disabled]:opacity-70 has-[>textarea:disabled]:opacity-70",
);

const INPUT_GROUP_CONTROL_CLASS = cn(
  "min-w-0 flex-1 border-0 bg-transparent text-foreground outline-none",
  "placeholder:text-foreground-muted",
  "focus:outline-none focus:ring-0",
  "disabled:cursor-not-allowed disabled:text-foreground-muted",
  INPUT_GROUP_CONTROL_TEXT_SIZE_CLASS,
);

export const INPUT_GROUP_INPUT_CLASS = cn(INPUT_GROUP_CONTROL_CLASS, "h-9 w-full px-3");

export const INPUT_GROUP_TEXTAREA_CLASS = cn(
  INPUT_GROUP_CONTROL_CLASS,
  "min-h-16 w-full resize-none px-3 py-2",
);

const INPUT_GROUP_ICON_CLASS =
  "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0";

export const INPUT_GROUP_ADDON_CLASS = cn(
  "flex items-center justify-center gap-2 text-foreground-muted select-none",
  INPUT_GROUP_ICON_CLASS,
);

export const INPUT_GROUP_ADDON_ALIGN_CLASS_MAP = {
  "inline-start": "order-first pl-3",
  "inline-end": "order-last pr-3",
  "block-start": "order-first w-full justify-between gap-2 px-3 pb-1.5 pt-3",
  "block-end": "order-last w-full justify-between gap-2 px-3 pb-3 pt-1.5",
} as const;

export const INPUT_GROUP_TEXT_CLASS = cn(
  "flex items-center gap-2 text-foreground-muted",
  INPUT_GROUP_ICON_CLASS,
  INPUT_GROUP_TEXT_SIZE_CLASS,
);

export const INPUT_GROUP_BUTTON_CLASS = cn(
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-sys-sm",
  "font-sys-default text-foreground transition-colors",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-primary",
  "disabled:pointer-events-none disabled:opacity-50",
  INPUT_GROUP_ICON_CLASS,
);

export const INPUT_GROUP_BUTTON_SIZE_CLASS_MAP = {
  xs: cn("h-6 gap-1 px-2 [&_svg]:size-3.5", INPUT_GROUP_BUTTON_XS_TEXT_SIZE_CLASS),
  sm: cn("h-8 gap-1.5 px-2.5", INPUT_GROUP_TEXT_SIZE_CLASS),
  "icon-xs": "size-6 p-0 [&_svg]:size-3.5",
  "icon-sm": "size-8 p-0",
} as const;

/*-------------------------------------
|  Types
-------------------------------------*/

export type TInputGroupProps = HTMLAttributes<HTMLDivElement>;

export type TInputGroupInputProps = InputHTMLAttributes<HTMLInputElement> & {
  error?: boolean;
};

export type TInputGroupTextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  error?: boolean;
};

export type TInputGroupAddonAlign = keyof typeof INPUT_GROUP_ADDON_ALIGN_CLASS_MAP;

export type TInputGroupAddonProps = HTMLAttributes<HTMLDivElement> & {
  align?: TInputGroupAddonAlign;
};

export type TInputGroupTextProps = HTMLAttributes<HTMLSpanElement>;

export type TInputGroupButtonVariant = TButtonVariant;
export type TInputGroupButtonSize = keyof typeof INPUT_GROUP_BUTTON_SIZE_CLASS_MAP;

export type TInputGroupButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: TInputGroupButtonVariant;
  size?: TInputGroupButtonSize;
};

type TInputGroupAddonDefaultProps = DefinedDefaults<TInputGroupAddonProps, "align">;

type TInputGroupButtonDefaultProps = DefinedDefaults<
  TInputGroupButtonProps,
  "variant" | "size"
>;

/*-------------------------------------
|  Defaults
-------------------------------------*/

export const INPUT_GROUP_ADDON_DEFAULTS = {
  align: "inline-start",
} satisfies TInputGroupAddonDefaultProps;

export const INPUT_GROUP_BUTTON_DEFAULTS = {
  variant: "tertiary",
  size: "xs",
} satisfies TInputGroupButtonDefaultProps;
